import React, { useState } from 'react';
import { useEditorStore } from '~/shell/Store';
import styled from 'styled-components';
import PopupMenu from '~/editor/popupMenu/PopupMenu';
import { EditBtn, EditBtnIcon } from '~/editor/buttons/EditBtn';
import DragAndDropIcon from '~/assets/button/dragAndDrop.svg';
import { useMoveStore } from '~/editor/store/moveElementStore';

interface MainContentProps {
  data: any;
}

const Container = styled.div`
  width: 90%;
  margin-left: auto;
  margin-right: auto;
  padding-top: 40px;

  .chapter {
    h2 {
      display: flex;
      align-items: center;
      white-space: nowrap;
      font-family: Recoleta;
      font-size: 24px;
      font-weight: 400;
      line-height: 28.8px;
      text-align: left;
      text-underline-position: from-font;
      text-decoration-skip-ink: none;
      margin-left: 10px;
      cursor: move;

      svg {
        margin-right: 8px;
      }
    }
  }

  .chapter_sequences {
    display: grid;
    grid-template-columns: repeat(auto-fill, 263px);
    gap: 20px 40px;
    padding: 10px;

    div {
      width: 263px;
      height: 180px;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      font-weight: bold;
      cursor: move;

      img {
        width: 263px;
        height: 151px;
        border-radius: 4px;
        border: 1px solid #a9a9a9;
      }

      span {
        font-family: Raleway;
        font-size: 12px;
        font-weight: 400;
        line-height: 18px;
        text-align: left;
      }
    }

    .dragElement {
      button {
        display: none;
      }
    }

    .dragElement:hover {
      button {
        display: block;
      }
    }
  }
`;

const Item = ({
  data,
  index,
  moveItem,
  chapterId,
  chapterSequenceId
}: {
  data: any;
  index: number;
  moveItem: (
    chapterId: any,
    chapterSequenceId: any,
    hoverIndex: number
  ) => void;
  training: any;
  setTraining: (data: any) => void;
  chapterId: any;
  chapterSequenceId: any;
  apiService: any;
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
    setIsPopupOpen(true);
  };

  const handleClose = () => {
    setIsPopupOpen(false);
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    useMoveStore.getState().startSequenceMove(chapterSequenceId);
    e.dataTransfer.setData(
      'sequence',
      JSON.stringify({ index, chapterId, chapterSequenceId })
    );
    setIsDragging(true);

    const currentElement = e.currentTarget as HTMLDivElement;
    const imgElement = currentElement.querySelector('img');

    let imgSrc = '';

    if (imgElement) {
      imgSrc = imgElement.src;
    }

    const dragImage = document.createElement('div');
    dragImage.className = 'dragImage';
    dragImage.style.width = '263px';
    dragImage.style.height = '180px';
    dragImage.style.borderRadius = '4px';
    dragImage.style.position = 'absolute';
    dragImage.style.top = '-9999px';
    dragImage.style.left = '-9999px';

    const textDiv = document.createElement('div');
    textDiv.style.backgroundImage = `url(${imgSrc})`;
    textDiv.style.backgroundSize = 'cover';
    textDiv.style.position = 'absolute';
    textDiv.style.bottom = '10px';
    textDiv.style.left = '10px';
    textDiv.style.color = 'white';
    textDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    textDiv.style.transform = 'rotate(-5deg)';

    if (dragImage) {
      dragImage.appendChild(textDiv);
    }

    const container = document.getElementById('dragAndDropContainer');
    if (container && dragImage) {
      container.appendChild(dragImage);
    }

    e.dataTransfer.setDragImage(dragImage, 131, 90);

    setTimeout(() => {
      if (container) {
        container.removeChild(dragImage);
      }
    }, 0);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    const draggedData = JSON.parse(e.dataTransfer.getData('sequence'));

    moveItem(chapterId, draggedData.chapterSequenceId, index);
    setIsDragging(false);

    setTimeout(() => {
      useMoveStore.getState().stopSequenceMove();
    }, 500);
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className="dragElement"
      style={{ position: 'relative' }}
    >
      <img
        src={data.sequence.screenshotUrl}
        alt={`Sequence ${index + 1}`}
        className={isDragging ? 'rotate' : ''}
      />
      <EditBtn onClick={handleButtonClick}>
        <EditBtnIcon />
      </EditBtn>
      <span>{(index + 1).toString().padStart(2, '0')}</span>
      <PopupMenu
        isOpen={isPopupOpen}
        anchorElement={anchorElement}
        onClose={handleClose}
        data={data}
      />
    </div>
  );
};

export const Overview: React.FC<MainContentProps> = ({ data }) => {
  const { training, setTraining, apiService } = useEditorStore();

  const moveItem = (
    targetChapterId: any,
    chapterSequenceId: any,
    hoverIndex: number
  ) => {
    const updatedChapters = [...training.chapters];
    const sourceChapterIndex = updatedChapters.findIndex(chapter =>
      chapter.chapterSequences.some(
        (sequence: any) => sequence.id === chapterSequenceId
      )
    );

    if (sourceChapterIndex !== -1) {
      const sourceChapter = updatedChapters[sourceChapterIndex];
      const dragIndex = sourceChapter.chapterSequences.findIndex(
        (sequence: any) => sequence.id === chapterSequenceId
      );
      const [draggedItem] = sourceChapter.chapterSequences.splice(dragIndex, 1);

      const targetChapterIndex = updatedChapters.findIndex(
        chapter => chapter.id === targetChapterId
      );

      if (targetChapterIndex !== -1) {
        const targetChapter = updatedChapters[targetChapterIndex];
        targetChapter.chapterSequences.splice(hoverIndex, 0, draggedItem);

        setTraining({ ...training, chapters: updatedChapters });

        if (apiService) {
          apiService.moveSequence(
            targetChapterId,
            chapterSequenceId,
            hoverIndex + 1
          );
        }
      }
    }
  };

  const handleChapterDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    chapterId: any
  ) => {
    e.dataTransfer.setData('chapter', JSON.stringify({ chapterId }));
  };

  const handleChapterDrop = (
    e: React.DragEvent<HTMLDivElement>,
    targetChapterId: any
  ) => {
    const { isSequenceMoving, chapterSequenceId } = useMoveStore.getState();
    if (isSequenceMoving) {
      const updatedChapters = [...training.chapters];
      const targetChapterIndex = updatedChapters.findIndex(
        chapter => chapter.id === targetChapterId
      );

      if (updatedChapters[targetChapterIndex].chapterSequences.length == 0) {
        moveItem(targetChapterId, chapterSequenceId, 1);
      }

      return;
    }

    const draggedData = JSON.parse(e.dataTransfer.getData('chapter'));
    const draggedChapterId = draggedData.chapterId;

    const updatedChapters = [...training.chapters];
    const draggedChapterIndex = updatedChapters.findIndex(
      chapter => chapter.id === draggedChapterId
    );
    const targetChapterIndex = updatedChapters.findIndex(
      chapter => chapter.id === targetChapterId
    );

    const newTraining = { ...training, chapters: updatedChapters };

    if (draggedChapterIndex !== -1 && targetChapterIndex !== -1) {
      const [draggedChapter] = updatedChapters.splice(draggedChapterIndex, 1);
      updatedChapters.splice(targetChapterIndex, 0, draggedChapter);
      setTraining(newTraining);
    }

    if (apiService && newTraining) {
      newTraining.chapters.map((chapter: any, index: number) => {
        if (chapter.id == draggedChapterId) {
          apiService.updateChapter(draggedChapterId, { position: index + 1 });
        }
      });
    }
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100vh',
        overflow: 'auto',
        padding: '30px',
        paddingTop: '70px',
        overflowY: 'auto'
      }}
    >
      <Container>
        {training.chapters.map((chapter: any, i: number) => {
          return (
            <div
              className="chapter"
              key={`chapter_${i}`}
              draggable
              onDragStart={e => handleChapterDragStart(e, chapter.id)}
              onDrop={e => handleChapterDrop(e, chapter.id)}
              onDragOver={e => e.preventDefault()}
            >
              <h2 key={`chapter_title_${i}`}>
                <DragAndDropIcon />
                {chapter.title}
              </h2>
              <div
                className="chapter_sequences"
                id="dragAndDropContainer"
                key={`chapter_sequences_${i}`}
              >
                {chapter.chapterSequences &&
                  chapter.chapterSequences.length > 0 &&
                  chapter.chapterSequences.map((el: any, index: number) => {
                    return (
                      <Item
                        apiService={apiService}
                        key={`item_${i}_${el.id}`}
                        data={el}
                        index={index}
                        moveItem={moveItem}
                        training={training}
                        setTraining={setTraining}
                        chapterId={chapter.id}
                        chapterSequenceId={el.id}
                      />
                    );
                  })}
              </div>
            </div>
          );
        })}
      </Container>
    </div>
  );
};
